import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Messenger, Message, UniversalHeader, BackButton } from '../../../shared/ui';
import EditableText from '../../../shared/ui/EditableText';
import QuestionMembersModal from './QuestionMembersModal';
import QuestionInfoPage from './QuestionInfoPage';
import { QuestionV2, questionsV2Api, QuestionMember } from '../../../api/questionsV2Api';
import { questionCommentsV2Api, QuestionCommentV2, CreateCommentRequest } from '../../../api/questionCommentsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import { useQuestionSignalR, QuestionStatusChangedEvent, QuestionMemberEvent, CommentEvent, CommentDeletedEvent } from '../../../hooks/useOptimizedSignalR';
import './QuestionV2MessengerModal.css';

interface QuestionV2MessengerModalProps {
  isOpen: boolean;
  question: QuestionV2 | null;
  siteId: number;
  onClose: () => void;
  onQuestionUpdated: () => void;
}

const QuestionV2MessengerModal: React.FC<QuestionV2MessengerModalProps> = ({
  isOpen,
  question,
  siteId,
  onClose,
  onQuestionUpdated
}) => {
  const [comments, setComments] = useState<QuestionCommentV2[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionText, setQuestionText] = useState(question?.text || '');
  const [members, setMembers] = useState<QuestionMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showInfoPage, setShowInfoPage] = useState(false);
  const [localQuestion, setLocalQuestion] = useState<QuestionV2 | null>(question);
  const { user } = useAuth();
  const { showBack, hideBack, state } = useNavigation();
  const onCloseRef = useRef(onClose);
  const savedBackHandlerRef = useRef<(() => void) | null>(null);
  const savedBackTransparentRef = useRef(false);
  
  // Обновляем ref при изменении onClose
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Перехватываем кнопку "Назад" только когда мессенджер ОТКРЫТ.
  // Когда закрыт - НЕ вызываем hideBack(), иначе скроем кнопку у родителя (FullscreenCardModal).
  useEffect(() => {
    if (isOpen) {
      if (state.back.show && state.back.onBack) {
        savedBackHandlerRef.current = state.back.onBack;
        savedBackTransparentRef.current = state.back.transparent;
      }
      showBack(() => onCloseRef.current(), true); // transparent для overlay поверх мессенджера
      return () => {
        const handler = savedBackHandlerRef.current;
        const transparent = savedBackTransparentRef.current;
        savedBackHandlerRef.current = null;
        hideBack();
        if (handler) {
          setTimeout(() => showBack(handler, transparent), 100);
        }
      };
    }
    // Когда isOpen=false - ничего не делаем, не вызываем hideBack()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Проверка, можно ли редактировать сообщение (не старше 24 часов)
  const canEditMessage = (createdAt: string) => {
    const messageDate = new Date(createdAt);
    const now = new Date();
    const diffHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);
    return diffHours < 24;
  };

  // Проверка, можно ли редактировать вопрос (только создатель, только в течение 24 часов)
  const canEditQuestion = () => {
    if (!question || !user) return false;
    
    // Проверяем, является ли пользователь создателем вопроса
    // Используем question.userId или question.user?.id
    const questionUserId = question.userId || question.user?.id;
    if (!questionUserId || questionUserId !== user.id) {
      return false;
    }
    
    // Проверяем время создания (не более 24 часов назад)
    if (question.createdAt) {
      const questionDate = new Date(question.createdAt);
      const now = new Date();
      const diffHours = (now.getTime() - questionDate.getTime()) / (1000 * 60 * 60);
      return diffHours < 24;
    }
    
    // Если нет даты создания, разрешаем редактирование (временно для тестирования)
    return true;
  };

  // Обновление текста вопроса при изменении
  useEffect(() => {
    if (question) {
      setQuestionText(question.text);
      setLocalQuestion(question);
    }
  }, [question]);

  // Обработчик SignalR событий для обновления статуса вопроса
  const handleQuestionStatusChanged = (questionEvent: QuestionStatusChangedEvent) => {
    if (localQuestion && questionEvent.id === localQuestion.id) {
      setLocalQuestion(prev => ({
        ...prev!,
        closed: questionEvent.closed
      }));
    }
  };

  // Обработчик SignalR событий для добавления участника
  const handleQuestionMemberAdded = (data: QuestionMemberEvent) => {
    console.log('🔔 QuestionV2MessengerModal: получено событие добавления участника', { data, localQuestion });
    if (localQuestion && data.question && data.question.id === localQuestion.id) {
      console.log('🔔 QuestionV2MessengerModal: участник добавлен', data.memberUserId);
      // Немедленно обновляем счетчик участников
      setMembers(prev => [...prev, { 
        userId: data.memberUserId, 
      }]);
      // Перезагружаем список участников для получения полных данных
      loadParticipants();
    } else {
      console.log('🔔 QuestionV2MessengerModal: событие не для текущего вопроса', { 
        localQuestionId: localQuestion?.id, 
        eventQuestionId: data.question?.id,
        hasQuestion: !!data.question 
      });
    }
  };

  // Обработчик SignalR событий для удаления участника
  const handleQuestionMemberRemoved = (data: QuestionMemberEvent) => {
    console.log('🔔 QuestionV2MessengerModal: получено событие удаления участника', { data, localQuestion });
    if (localQuestion && data.question && data.question.id === localQuestion.id) {
      console.log('🔔 QuestionV2MessengerModal: участник удален', data.memberUserId);
      
      // Проверяем, не удалили ли текущего пользователя
      if (data.memberUserId === user?.id) {
        console.log('🔔 QuestionV2MessengerModal: текущий пользователь удален из чата - отключаемся от группы');
        // Отключаемся от группы вопроса
        if (typeof window !== 'undefined' && (window as any).notificationClient) {
          const client = (window as any).notificationClient;
          client.leaveQuestionGroup(localQuestion.id);
          console.log(`📡 Отключились от группы вопроса ${localQuestion.id} - пользователь удален`);
        }
        // Закрываем модальное окно чата
        onClose();
        return;
      }
      
      // Немедленно обновляем счетчик участников
      setMembers(prev => prev.filter(p => p.userId !== data.memberUserId));
      // Перезагружаем список участников для получения полных данных
      loadParticipants();
    } else {
      console.log('🔔 QuestionV2MessengerModal: событие не для текущего вопроса', { 
        localQuestionId: localQuestion?.id, 
        eventQuestionId: data.question?.id,
        hasQuestion: !!data.question 
      });
    }
  };

  // Обработчик SignalR событий для создания комментария
  const handleCommentCreated = (comment: CommentEvent) => {
    console.log('🔔 QuestionV2MessengerModal: получено событие создания комментария', { comment, localQuestion });
    if (localQuestion && comment.questionId === localQuestion.id) {
      console.log('🔔 QuestionV2MessengerModal: новый комментарий добавлен', comment.id);
      
      // Проверяем, есть ли уже этот комментарий (оптимистичное обновление)
      setComments(prev => {
        const exists = prev.some(c => c.id === comment.id);
        if (exists) {
          // Обновляем существующий комментарий (на случай оптимистичного обновления)
          return prev.map(c => c.id === comment.id ? {
            ...c,
            ...comment,
            userName: c.userName, // Сохраняем имя пользователя если оно уже есть
            avatarUrl: c.avatarUrl // Сохраняем аватар если он уже есть
          } : c);
        } else {
          // Добавляем новый комментарий, но нужны userName и avatarUrl
          // Так как их нет в CommentEvent, загружаем полные данные
          loadComments();
          return prev;
        }
      });
    } else {
      console.log('🔔 QuestionV2MessengerModal: событие не для текущего вопроса', { 
        localQuestionId: localQuestion?.id, 
        eventQuestionId: comment.questionId
      });
    }
  };

  // Обработчик SignalR событий для обновления комментария
  const handleCommentUpdated = (comment: CommentEvent) => {
    console.log('🔔 QuestionV2MessengerModal: получено событие обновления комментария', { comment, localQuestion });
    if (localQuestion && comment.questionId === localQuestion.id) {
      console.log('🔔 QuestionV2MessengerModal: комментарий обновлен', comment.id);
      
      // Обновляем комментарий в локальном состоянии без перезагрузки
      setComments(prev => prev.map(c => {
        if (c.id === comment.id) {
          return {
            ...c,
            text: comment.text,
            isEdited: comment.isEdited,
            editedAt: comment.editedAt
          };
        }
        return c;
      }));
    } else {
      console.log('🔔 QuestionV2MessengerModal: событие не для текущего вопроса', { 
        localQuestionId: localQuestion?.id, 
        eventQuestionId: comment.questionId
      });
    }
  };

  // Обработчик SignalR событий для удаления комментария
  const handleCommentDeleted = (data: CommentDeletedEvent) => {
    console.log('🔔 QuestionV2MessengerModal: получено событие удаления комментария', { data, localQuestion });
    if (localQuestion && data.questionId === localQuestion.id) {
      console.log('🔔 QuestionV2MessengerModal: комментарий удален', data.commentId);
      // Немедленно удаляем комментарий из списка
      setComments(prev => prev.filter(c => c.id !== data.commentId));
    } else {
      console.log('🔔 QuestionV2MessengerModal: событие не для текущего вопроса', { 
        localQuestionId: localQuestion?.id, 
        eventQuestionId: data.questionId
      });
    }
  };

  // Используем оптимизированный SignalR хук для подписки на события конкретного вопроса
  useQuestionSignalR(localQuestion?.id || 0, {
    onQuestionStatusChanged: handleQuestionStatusChanged,
    onQuestionMemberAdded: handleQuestionMemberAdded,
    onQuestionMemberRemoved: handleQuestionMemberRemoved,
    onCommentCreated: handleCommentCreated,
    onCommentUpdated: handleCommentUpdated,
    onCommentDeleted: handleCommentDeleted,
  });

  // Преобразование комментариев в сообщения для универсального Messenger
  const messages: Message[] = comments.map(comment => ({
    id: comment.id,
    userId: comment.userId,
    userName: comment.userName || 'Неизвестный',
    text: comment.text,
    createdAt: comment.createdAt,
    isEdited: comment.isEdited,
    editedAt: comment.editedAt,
    canEdit: comment.userId === user?.id, // Только автор может редактировать
    canDelete: comment.userId === user?.id, // Только автор может удалять
    avatarUrl: comment.avatarUrl
  }));

  // Загрузка комментариев при открытии модального окна
  useEffect(() => {
    if (question && isOpen) {
      loadComments();
      loadParticipants();
    }
  }, [question, isOpen]);

  const loadComments = async () => {
    if (!question) return;
    
    try {
      setCommentsLoading(true);
      setError(null);
      const commentsData = await questionCommentsV2Api.getQuestionComments(siteId, question.id);
      setComments(commentsData);
    } catch (err) {
      console.error('Error loading comments:', err);
      setError('Ошибка загрузки комментариев');
    } finally {
      setCommentsLoading(false);
    }
  };

  // Загрузка участников вопроса
  const loadParticipants = async () => {
    if (!question) return;
    
    try {
      setMembersLoading(true);
      const membersData = await questionsV2Api.getQuestionMembers(siteId, question.id);
      setMembers(membersData);
    } catch (err) {
      console.error('Error loading members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  // Клик по шапке / «N участников» — открываем список участников чата (с кнопкой «Права»)
  const handleParticipantsClick = () => {
    setShowParticipantsModal(true);
  };

  // Кнопка меню в шапке — информация о вопросе/заметке (закрыть, открыть, и т.д.)
  const handleInfoMenuClick = () => {
    setShowInfoPage(true);
  };

  const handleAddParticipant = async (userId: number) => {
    if (!question) return;
    
    try {
      await questionsV2Api.addQuestionMember(siteId, question.id, userId);
      await loadParticipants(); // Перезагружаем список участников
    } catch (err: any) {
      console.error('Error adding participant:', err);
      setError('Ошибка добавления участника');
    }
  };

  const handleParticipantsModalClose = () => {
    setShowParticipantsModal(false);
  };

  const handleInfoPageClose = () => {
    setShowInfoPage(false);
  };

  // Функции для универсального Messenger
  const handleSendMessage = async (text: string) => {
    if (!question || !text.trim() || !user) return;
    
    try {
      setError(null);
      
      const commentData: CreateCommentRequest = {
        text: text.trim()
      };
      
      // Создаем временный ID для оптимистичного комментария
      const tempId = -Date.now();
      
      // Оптимистичное обновление: сразу добавляем комментарий в UI
      const optimisticComment: QuestionCommentV2 = {
        id: tempId,
        questionId: question.id,
        userId: user.id,
        userName: user.name,
        text: text.trim(),
        createdAt: new Date().toISOString(),
        isEdited: false
        // avatarUrl будет добавлен при обновлении с сервера через SignalR
      };
      
      setComments(prev => [...prev, optimisticComment]);
      
      // Отправляем комментарий на сервер
      const result = await questionCommentsV2Api.addComment(siteId, question.id, commentData);
      
      // Обновляем временный комментарий на реальный ID
      setComments(prev => prev.map(c => 
        c.id === tempId ? { ...c, id: result.id } : c
      ));
      
      // SignalR событие также может прийти, но мы уже обработали это в handleCommentCreated
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError('Ошибка отправки сообщения');
      // При ошибке удаляем оптимистичный комментарий
      setComments(prev => prev.filter(c => c.id >= 0));
    }
  };

  // Проверка возможности редактирования сообщения
  const handleStartEdit = (messageId: number | string): boolean => {
    const comment = comments.find(c => c.id === Number(messageId));
    if (comment && !canEditMessage(comment.createdAt)) {
      setError('Нельзя редактировать сообщения, которым более 24 часов');
      return false;
    }
    return true;
  };

  const handleEditMessage = async (messageId: number | string, newText: string) => {
    if (!question || !newText.trim()) return;
    
    try {
      setError(null);
      
      // Оптимистичное обновление: сразу обновляем комментарий в UI
      setComments(prev => prev.map(c => {
        if (c.id === Number(messageId)) {
          return {
            ...c,
            text: newText.trim(),
            isEdited: true,
            editedAt: new Date().toISOString()
          };
        }
        return c;
      }));
      
      // Отправляем изменения на сервер
      await questionCommentsV2Api.updateComment(siteId, question.id, Number(messageId), {
        text: newText.trim()
      });
      
      // SignalR событие также может прийти, но мы уже обработали это локально
    } catch (err: any) {
      console.error('Error editing message:', err);
      setError('Ошибка редактирования сообщения');
      // При ошибке откатываем изменения загрузкой с сервера
      await loadComments();
    }
  };

  const handleCloseQuestion = async () => {
    if (!question) return;
    
    try {
      await questionsV2Api.closeQuestion(siteId, question.id);
      onQuestionUpdated(); // Обновляем список вопросов
      onClose(); // Закрываем модальное окно
    } catch (err: any) {
      console.error('Error closing question:', err);
      setError(`Ошибка закрытия ${getContentTypeNameGenitive()}`);
    }
  };

  const handleOpenQuestion = async () => {
    if (!question) return;
    
    try {
      await questionsV2Api.reopenQuestion(siteId, question.id);
      onQuestionUpdated(); // Обновляем список вопросов
    } catch (err: any) {
      console.error('Error opening question:', err);
      setError(`Ошибка открытия ${getContentTypeNameGenitive()}`);
    }
  };

  // Сохранение изменений текста вопроса
  const handleQuestionTextSave = async (newText: string) => {
    if (!question) return;
    
    try {
      await questionsV2Api.editQuestionText(siteId, question.id, newText);
      setQuestionText(newText);
      onQuestionUpdated(); // Обновляем данные вопроса
    } catch (err: any) {
      console.error('Error saving question text:', err);
      throw err; // Пробрасываем ошибку для отображения в EditableText
    }
  };

  const handleErrorClose = () => {
    setError(null);
  };

  // Функция для получения названия типа контента
  const getContentTypeName = () => {
    switch (localQuestion?.typeId) {
      case 1:
        return 'дискуссию';
      case 2:
        return 'заметку';
      case 3:
        return 'заявку';
      default:
        return 'дискуссию';
    }
  };

  // Функция для получения названия типа контента в родительном падеже
  const getContentTypeNameGenitive = () => {
    switch (localQuestion?.typeId) {
      case 1:
        return 'дискуссии';
      case 2:
        return 'заметки';
      case 3:
        return 'заявки';
      default:
        return 'дискуссии';
    }
  };

  // Функция для получения названия обсуждения
  const getDiscussionTitle = () => {
    switch (localQuestion?.typeId) {
      case 1:
        return 'Дискуссия';
      case 2:
        return 'Обсуждение заметки';
      case 3:
        return 'Обсуждение заявки';
      default:
        return 'Дискуссия';
    }
  };

  // Функция для получения текста статуса
  const getStatusText = (isClosed: boolean) => {
    switch (localQuestion?.typeId) {
      case 1:
        return isClosed ? 'Закрыт' : 'Активен';
      case 2:
        return isClosed ? 'Закрыта' : 'Активна';
      case 3:
        return isClosed ? 'Выполнена' : 'В работе';
      default:
        return isClosed ? 'Закрыт' : 'Активен';
    }
  };

  // Функция для получения правильного названия участников с окончаниями
  const getMembersLabel = (count: number) => {
    // Для всех типов контента используем "участник"
    if (count === 1) {
      return 'участник';
    } else if (count >= 2 && count <= 4) {
      return 'участника';
    } else {
      return 'участников';
    }
  };
  const updateQuestionStatus = (closed: boolean) => {
    if (localQuestion) {
      setLocalQuestion({
        ...localQuestion,
        closed
      });
    }
  };



  if (!localQuestion || !isOpen) {
    return null;
  }

  return (
    <>
      {createPortal(
        <div className="question-messenger-fullscreen">
          {/* Локальная кнопка «Назад» в том же месте, что и глобальная — на случай если глобальная не отображается */}
          <div className="question-messenger-back-button">
            <BackButton
              variant="arrow"
              size="medium"
              onClick={onClose}
              transparent={true}
            />
          </div>
          <UniversalHeader
            title={`${getContentTypeName()} от ${localQuestion.user?.name || 'Неизвестный'}`}
            onBack={onClose}
            onMenu={handleInfoMenuClick}
            showBackButton={true}
            showMenu={true}
            onClick={handleParticipantsClick}
            clickable={true}
            centered={true}
            infoRow={{
              members: {
                count: members.length,
                onClick: handleParticipantsClick,
                getLabel: getMembersLabel
              },
              status: {
                text: getStatusText(localQuestion.closed),
                type: localQuestion.closed ? 'closed' : 'active'
              }
            }}
          />

          {/* Мессенджер без дополнительных контейнеров */}
          <Messenger
            messages={messages}
            loading={commentsLoading}
            error={error}
            onErrorClose={handleErrorClose}
            onSendMessage={handleSendMessage}
            onEditMessage={handleEditMessage}
            onStartEdit={handleStartEdit}
            placeholder={`Напишите комментарий к ${getContentTypeNameGenitive()}...`}
            maxLength={1000}
            className="question-messenger-chat"
            headerContent={
              <div className="question-text-content">
                <EditableText
                  text={questionText}
                  onSave={handleQuestionTextSave}
                  placeholder={`Введите текст ${getContentTypeNameGenitive()}...`}
                  maxLength={1000}
                  canEdit={canEditQuestion()}
                  editButtonTitle={`Редактировать ${getContentTypeName()}`}
                  className="question-text-editable"
                />
              </div>
            }
          />

          {/* Страница информации о вопросе */}
          <QuestionInfoPage
            isOpen={showInfoPage}
            question={localQuestion}
            siteId={siteId}
            onClose={handleInfoPageClose}
            onQuestionUpdated={() => {
              onQuestionUpdated?.();
              // Обновляем локальное состояние после изменения статуса
              if (localQuestion) {
                setLocalQuestion({
                  ...localQuestion,
                  closed: !localQuestion.closed
                });
              }
            }}
          />
        </div>,
        document.body
      )}
      
      {/* Модальное окно участников - рендерится отдельно, вне Portal чата */}
      <QuestionMembersModal
        isOpen={showParticipantsModal}
        siteId={siteId}
        questionId={localQuestion?.id || 0}
        typeId={localQuestion?.typeId || 1}
        members={members}
        onClose={handleParticipantsModalClose}
        onMembersUpdated={loadParticipants}
      />
    </>
  );
};

export default QuestionV2MessengerModal;
